'use client';

import classNames from 'classnames';
import { useEffect, useId, useState } from 'react';
import RichText from '@/components/atoms/rich-text/rich-text';
import Spinner from '@/components/atoms/spinner/spinner';
import Typography from '@/components/atoms/typography/typography';
import { useI18n } from '@/libraries/contexts/i18n-context';
import useHubspotScript from '@/libraries/hooks/use-hubspot-script';
// eslint-disable-next-line css-modules/no-unused-class
import styles from './hubspot-form-item.module.scss';

export interface HubspotFormItemProps {
  /** The class name to apply */
  className?: string;
  /** The form to show */
  form: Content.Components['HubspotFormDto'];
  /** The information to push into the form */
  information?: string;
  /** If the form is rendered in lightbox */
  isLightbox?: boolean;
  /** If the form is rendered in tab view */
  isTabbedView?: boolean;
  /** Indicates whether the hubspot form should not have padding */
  noPadding?: boolean;
  /** The variant of the component to use */
  variant?: Content.Components['ModuleVariant'];
}

const HubspotFormItem = ({
  className,
  form,
  information,
  isLightbox,
  isTabbedView,
  noPadding = false,
  variant,
}: HubspotFormItemProps) => {
  const { t } = useI18n();
  const isHubspotScriptReady = useHubspotScript();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const formInstanceId = useId();
  const formTarget = `form-${form.id}-${formInstanceId.replace(/:/g, '')}`;

  useEffect(() => {
    if (!isHubspotScriptReady || !window.hbspt) return;

    let isDisposed = false;

    const handleFormReady = () => {
      if (isDisposed) return;

      setIsLoading(false);

      const FORM_TIMEOUT_OFFSET = 1000;

      setTimeout(() => {
        const targetElement = document.querySelector(`.${formTarget}`);
        const hiddenField = targetElement?.querySelector(
          'input[name="onehome"]',
        );

        if (hiddenField && information) {
          hiddenField.setAttribute('value', information);
        }
      }, FORM_TIMEOUT_OFFSET);
    };

    const handleFormSubmitted = () => {
      setIsSubmitted(true);

      const targetElement = document.getElementById(formTarget);

      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    const createForm = () => {
      if (isDisposed || !window.hbspt) return;

      const targetElement = document.querySelector(`.${formTarget}`);

      if (!targetElement) return;

      targetElement.innerHTML = '';

      window.hbspt.forms.create({
        formId: `${form.settings.formId}`,
        formInstanceId: `${formInstanceId}`,
        onFormReady: handleFormReady,
        onFormSubmitted: handleFormSubmitted,
        portalId: `${form.settings.portalId}`,
        region: `${form.settings.region}`,
        target: `.${formTarget}`,
      });
    };

    createForm();

    const onReady = (event: any) => {
      if (event.detail.formId !== form.settings.formId) return;

      handleFormReady();
    };

    const onSubmitted = (event: any) => {
      if (event.detail.formId !== form.settings.formId) return;

      handleFormSubmitted();
    };

    window.addEventListener('hs-form-event:on-ready', onReady);

    window.addEventListener('hs-form-event:on-submission:success', onSubmitted);

    return () => {
      isDisposed = true;

      window.removeEventListener('hs-form-event:on-ready', onReady);
      window.removeEventListener(
        'hs-form-event:on-submission:success',
        onSubmitted,
      );
    };
  }, [
    isHubspotScriptReady,
    form.settings.formId,
    form.settings.portalId,
    form.settings.region,
    formInstanceId,
    formTarget,
    information,
  ]);

  return (
    <div
      className={classNames(styles.component, className, {
        [styles.hasCustomStyling]: !form.useHubspotStyling,
        [styles.hasNoPadding]: noPadding,
        [styles.isLightbox]: isLightbox,
        [styles.isSurface]: variant === 'surface',
        [styles.isTabbedView]: isTabbedView,
      })}
    >
      {(form.headline || form.submittedHeadline) && (
        <Typography
          className={styles.headline}
          id={formTarget}
          tagStyle="headingMedium"
        >
          {isSubmitted ? form.submittedHeadline : form.headline}
        </Typography>
      )}

      {form.text && <RichText className={styles.text} text={form.text} />}

      {isLoading && (
        <div className={styles.spinner}>
          <Spinner loadingText={`${t('items.common.words.loading')}...`} />
        </div>
      )}

      <div
        className={classNames(styles.form, {
          [styles.isVisible]: !isLoading,
        })}
      >
        <div className={formTarget} />
      </div>
    </div>
  );
};

HubspotFormItem.displayName = 'HubspotFormItem';

export default HubspotFormItem;
